/**
 * React Query hooks for Dashboard
 *
 * Production-grade hooks with:
 * - Auto-refresh every 60 seconds
 * - Singleton service instances
 * - Manual refetch support
 */

import { useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth';
import { DashboardService } from '../services/dashboard';

// Retry config to avoid permanent loading states
const RETRY_COUNT = 2;
const RETRY_DELAY = 1000;

// Query key factory for consistent key management
export const dashboardKeys = {
  all: ['dashboard'],
  stats: (is_sandbox) => [...dashboardKeys.all, 'stats', { is_sandbox }],
  screeningSummary: (is_sandbox) => [...dashboardKeys.all, 'screening-summary', { is_sandbox }],
  activity: (limit, is_sandbox) => [...dashboardKeys.all, 'activity', { limit, is_sandbox }],
};

// Auto-refresh interval (60 seconds)
const REFETCH_INTERVAL = 60000;
const STALE_TIME = 60000;

/**
 * Hook to get a memoized DashboardService instance
 * Prevents recreating service on every render
 */
function useDashboardService() {
  const { getToken, isAuthenticated } = useAuth();
  const service = useMemo(() => new DashboardService(getToken), [getToken]);
  return { service, isAuthenticated };
}

/**
 * Hook to fetch dashboard KPI statistics
 * Auto-refreshes every 60 seconds
 * @param {Object} params - Parameters including is_sandbox
 * @param {Object} options - Additional React Query options
 */
export function useDashboardStats({ is_sandbox = false } = {}, options = {}) {
  const { service, isAuthenticated } = useDashboardService();

  return useQuery({
    queryKey: dashboardKeys.stats(is_sandbox),
    queryFn: ({ signal }) => service.getStats({ is_sandbox, signal }),
    staleTime: STALE_TIME,
    refetchInterval: REFETCH_INTERVAL,
    enabled: isAuthenticated,
    retry: RETRY_COUNT,
    retryDelay: RETRY_DELAY,
    ...options,
  });
}

/**
 * Hook to fetch screening hit summary
 * Auto-refreshes every 60 seconds
 * @param {Object} params - Parameters including is_sandbox
 * @param {Object} options - Additional React Query options
 */
export function useScreeningSummary({ is_sandbox = false } = {}, options = {}) {
  const { service, isAuthenticated } = useDashboardService();

  return useQuery({
    queryKey: dashboardKeys.screeningSummary(is_sandbox),
    queryFn: ({ signal }) => service.getScreeningSummary({ is_sandbox, signal }),
    staleTime: STALE_TIME,
    refetchInterval: REFETCH_INTERVAL,
    enabled: isAuthenticated,
    retry: RETRY_COUNT,
    retryDelay: RETRY_DELAY,
    ...options,
  });
}

/**
 * Hook to fetch recent activity feed
 * Auto-refreshes every 60 seconds
 * @param {number} limit - Number of items to fetch
 * @param {Object} params - Parameters including is_sandbox
 * @param {Object} options - Additional React Query options
 */
export function useRecentActivity(limit = 20, { is_sandbox = false } = {}, options = {}) {
  const { service, isAuthenticated } = useDashboardService();

  return useQuery({
    queryKey: dashboardKeys.activity(limit, is_sandbox),
    queryFn: ({ signal }) => service.getActivity(limit, { is_sandbox, signal }),
    staleTime: STALE_TIME,
    refetchInterval: REFETCH_INTERVAL,
    enabled: isAuthenticated,
    retry: RETRY_COUNT,
    retryDelay: RETRY_DELAY,
    ...options,
  });
}

/**
 * Hook to manually refresh all dashboard data
 * Returns a function that invalidates all dashboard queries
 */
export function useRefreshDashboard() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
  }, [queryClient]);
}

/**
 * Convenience hook that fetches all dashboard data at once
 * Useful for getting loading/error states in one place
 */
export function useDashboardData() {
  const stats = useDashboardStats();
  const screening = useScreeningSummary();
  const activity = useRecentActivity();
  const refresh = useRefreshDashboard();

  return {
    stats,
    screening,
    activity,
    refresh,
    isLoading: stats.isLoading || screening.isLoading || activity.isLoading,
    isError: stats.isError || screening.isError || activity.isError,
    error: stats.error || screening.error || activity.error,
  };
}
